import * as React from 'react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@radix-ui/react-collapsible';
import {
  StackLayout,
  Box,
  Button,
  GDSStyleObject,
  Icon,
} from '@leagueplatform/genesis-core';
import { ChevronDownFilledIcon } from '../../assets/chevron-down-filled-icon';

export interface CollapsibleSectionProps {
  label: string;
  expandedLabel?: string;
  css?: GDSStyleObject;
  trailingContent?: React.ReactNode;
  triggerPriority?: 'primary' | 'secondary' | 'tertiary';
}

export const CollapsibleSection = ({
  label,
  children,
  css,
  expandedLabel,
  trailingContent,
  triggerPriority = 'tertiary',
}: React.PropsWithChildren<CollapsibleSectionProps>) => {
  const [open, setOpen] = React.useState(false);
  const triggerLabel = open && expandedLabel ? expandedLabel : label;

  return (
    <Box
      as={Collapsible}
      open={open}
      onOpenChange={setOpen}
      css={{
        '.collapsible-trigger-button': {
          '&:hover': {
            backgroundColor: '$none',
            color: '$onSurfaceTextPrimary',
            borderColor: 'transparent',
          },
          '&:active': {
            backgroundColor: '$none',
            color: '$onSurfaceTextPrimary',
            borderColor: 'transparent',
          },
        },
        '.collapsible-button-icon': {
          transition: 'transform 200ms',
        },
        '&[data-state="open"] .collapsible-button-icon': {
          transform: 'rotate(180deg)',
        },
        ...css,
      }}
    >
      <StackLayout
        orientation="horizontal"
        spacing="$quarter"
        verticalAlignment="center"
      >
        <Button
          as={CollapsibleTrigger}
          size="small"
          quiet
          priority={triggerPriority}
          className="collapsible-trigger-button"
          aria-label={`${triggerLabel}${open ? ' (expanded)' : ' (collapsed)'}`}
        >
          <StackLayout
            orientation="horizontal"
            spacing="$half"
            verticalAlignment="center"
          >
            <Icon
              css={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              className="collapsible-button-icon"
              size="$half"
              icon={<ChevronDownFilledIcon />}
            />
            <span>{triggerLabel}</span>
          </StackLayout>
        </Button>
        {trailingContent}
      </StackLayout>
      <Box
        as={CollapsibleContent}
        css={{
          paddingBlockStart: '$half',
          paddingInlineStart: '1.25rem',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};
